// client/src/api/FlexFetch.js

import {
  isMobilePlatform,
  getMobileAuthToken,
  clearMobileAuthToken,
} from '../utils/mobileAuth.js'

function isPrivateNetworkHostname(hostname) {
  return (
    hostname?.startsWith('10.') ||
    hostname?.startsWith('192.168.') ||
    hostname === 'servesync.local'
  )
}

function resolveConfiguredApiBaseUrl(configuredUrl) {
  if (typeof window === 'undefined') {
    return configuredUrl
  }

  try {
    const configured = new URL(configuredUrl)
    const pageHostname = window.location?.hostname

    if (
      isPrivateNetworkHostname(pageHostname) &&
      (configured.hostname === 'localhost' ||
        configured.hostname === '127.0.0.1')
    ) {
      configured.hostname = pageHostname
      return configured.toString().replace(/\/$/, '')
    }
  } catch {
    return configuredUrl
  }

  return configuredUrl
}

function resolveLocalDevelopmentApiBaseUrl() {
  const pagePort = Number(window.location.port)

  if (Number.isInteger(pagePort) && pagePort >= 3000 && pagePort < 4000) {
    const apiPort = 8000 + (pagePort - 3000)
    return `http://${window.location.hostname}:${apiPort}`
  }

  return `http://${window.location.hostname}:8000`
}

/**
 * Determines the API base URL based on environment.
 * Priority:
 * 1. REACT_APP_API_URL env var (always wins if set)
 * 2. Capacitor detection (prevents localhost:8000 in mobile)
 * 3. Local development detection (localhost, private IPs, local domains)
 * 4. Production fallback (same origin or elasticbeanstalk)
 */
function getApiBaseUrl() {
  // Always prefer explicit configuration
  if (process.env.REACT_APP_API_URL) {
    return resolveConfiguredApiBaseUrl(process.env.REACT_APP_API_URL)
  }

  // In Capacitor, we must NOT use localhost:8000
  // The REACT_APP_API_URL should be set during build, but if not set,
  // we'll throw an error to prevent silent failures
  if (isMobilePlatform()) {
    throw new Error(
      'Capacitor Mobile Configuration Error: REACT_APP_API_URL is required but not set.\n\n' +
        'To fix this, set the API URL before building:\n' +
        '  export REACT_APP_API_URL=https://api.servesynctennis.com\n' +
        '  npm run build\n\n' +
        "For local development with mobile, use your computer's IP:\n" +
        '  export REACT_APP_API_URL=http://192.168.1.100:8000\n\n' +
        'See README.md "Mobile App (Capacitor/iOS)" section for more details.',
    )
  }

  // Standard web development detection
  const isLocalDevelopment =
    window.location.hostname === 'localhost' ||
    window.location.hostname.startsWith('10.0.0.') ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.includes('servesync.local')

  if (isLocalDevelopment) {
    return resolveLocalDevelopmentApiBaseUrl()
  }

  // Production environment
  if (window.location.hostname.includes('elasticbeanstalk.com')) {
    return window.location.origin
  }

  return `${window.location.protocol}//${window.location.hostname}`
}

// Lazy initialization of basePath to allow testing and prevent
// module evaluation errors when Capacitor is detected without URL config
let basePath = null
let basePathInitialized = false

function getBasePath() {
  if (!basePathInitialized) {
    basePath = getApiBaseUrl()
    basePathInitialized = true
  }
  return basePath
}

// Global handler for authentication failures
let onAuthFailure = null

export function setAuthFailureHandler(handler) {
  onAuthFailure = handler
}

async function flexFetch(path, options = {}) {
  // Clone headers to avoid mutating caller's object
  const headers = { ...(options.headers || {}) }

  // If mobile platform, add Bearer token authorization
  if (isMobilePlatform()) {
    const token = getMobileAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  // If sending FormData, let the browser set the correct multipart boundary.
  // Otherwise, default to JSON if caller hasn't explicitly set a Content-Type.
  const isFormData =
    typeof FormData !== 'undefined' && options.body instanceof FormData
  if (!isFormData) {
    const hasContentType = headers['Content-Type'] || headers['content-type']
    if (!hasContentType) {
      headers['Content-Type'] = 'application/json; charset=utf-8'
    }
  }
  const url = getBasePath() + path
  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  })

  return res
}

async function handleResponse(response) {
  // Clone the response so we can safely attempt multiple reads
  const respClone = response.clone()
  let responseJson
  try {
    responseJson = await respClone.json()
  } catch {
    // If the response is not JSON (e.g., HTML error page), fall back to raw text
    try {
      const raw = await response.text() // Use original response, not clone
      responseJson = { error: raw }
    } catch {
      responseJson = { error: 'Unable to read response body' }
    }
  }
  if (response.status >= 400) {
    if (response.status >= 500) {
      const err = new Error(
        'There was an unexpected error. Please try again later.',
      )
      err.response = { status: response.status, data: responseJson }
      throw err
    }

    if (response.status === 401) {
      // Clear mobile auth token on authentication failure
      if (isMobilePlatform()) {
        clearMobileAuthToken()
      }
      // Trigger global auth failure handler if registered
      if (onAuthFailure) {
        onAuthFailure()
      }
      const err = new Error('You are not authorized to perform this action.')
      err.response = { status: response.status, data: responseJson }
      throw err
    }

    const errorInfo =
      responseJson?.success === false
        ? responseJson.error
        : responseJson.error || responseJson
    const errorMessage =
      typeof errorInfo === 'string'
        ? errorInfo
        : errorInfo?.message || 'Request failed'
    const err = new Error(errorMessage)
    err.response = { status: response.status, data: responseJson }
    throw err
  }
  return responseJson
}

const flex = {
  flexFetch,
  handleResponse,
}

// Export helper functions for testing
export { getApiBaseUrl }

// Reset cached basePath for testing
export function resetBasePathCache() {
  basePath = null
  basePathInitialized = false
}

export default flex
